<template>
    <div class="conversation-viewer">
      <div class="viewer-header">
        <div class="viewer-title-area">
          <h3 class="viewer-title">Conversation</h3>
          <span v-if="parsedConversation.length > 0" class="segment-count">({{ parsedConversation.length }} segments)</span>
        </div>
        <button class="toggle-btn" @click="toggleViewer">
          {{ isViewerExpanded ? '▼' : '▶' }}
        </button>
      </div>

      <div v-show="isViewerExpanded" class="viewer-content">
        <div v-if="parsedConversation.length > 0" class="conversation" ref="conversationContainer" :class="{ 'is-playing': isPlaying, 'is-expanded': isExpandedView }">
          <transition-group name="fade" mode="out-in">
            <div
              v-for="(segment, idx) in parsedConversation"
              :key="idx"
              class="segment"
              :class="{
                active: idx === activeSegment,
                'segment-host': segment.role && segment.role.toLowerCase().includes('host'),
                'segment-expert': segment.role && !segment.role.toLowerCase().includes('host'),
                'is-playing': isPlaying && idx === activeSegment,
                'is-expanded': isExpandedView,
                'has-custom-recording': customRecordings.has(idx)
              }"
              :ref="el => setSegmentRef(el, messageId, idx)"
              @click="activateSegment(idx, $event)"
            >
              <section-type-editor
                v-if="!isPlaying && idx === activeSegment"
                :message-id="messageId"
                :segment-index="idx"
                :current-section="segment.section"
                @section-updated="handleSectionUpdated"
              />
              <segment-editing-buttons
                v-if="!isPlaying && idx === activeSegment"
                :is-visible="true"
                :message-id="messageId"
                :segment-index="idx"
                :current-speaker="{
                  name: segment.speaker,
                  role: segment.role
                }"
                :current-section="segment.section"
                :segments="parsedConversation"
                @segment-deleted="handleSegmentDeleted"
                @segment-added="handleSegmentAdded"
              />

              <div class="speaker-info">
                <span class="speaker-name" :style="{ color: getWaveBarColor(idx) }">{{ segment.speaker }}</span>
                <span class="segment-number">#{{ idx + 1 }}</span>
                <span class="speaker-role">({{ segment.role || 'Unknown' }})</span>

                <span v-if="customRecordings.has(idx)" class="custom-voice-badge" title="Custom voice recording">
                  🎤
                </span>

                <div v-if="isLoading && idx === activeSegment" class="loading-indicator">
                  <div class="loading-spinner" :style="{ 'border-top-color': getWaveBarColor(idx) }"></div>
                  <span class="loading-text" :style="{ color: getWaveBarColor(idx) }">Loading audio...</span>
                </div>

                <div v-if="isPlaying && idx === activeSegment" class="playing-indicator">
                  <div class="wave-bars">
                    <div class="wave-bar"
                        v-for="i in 3"
                        :key="i"
                        :style="{ '--wave-color': getWaveBarColor(idx) }"></div>
                  </div>
                  <span class="playing-text" :style="{ color: getWaveBarColor(idx) }">
                    {{ customRecordings.has(idx) ? 'Custom Recording' : 'Speaking' }}
                  </span>
                </div>
              </div>

              <citation-renderer
                :html="segment.content"
                :is-editable="!isPlaying && idx === activeSegment"
                :segment-index="idx"
                :max-tokens="510"
                :chat-id="chatId"
                @citation-click="handleCitationClick"
                @citation-speaking="handleCitationSpeaking"
                @auto-scroll-complete="handleAutoScrollComplete"
                @full-audio-ready="handleFullAudioReady"
                @audio-progress="handleAudioProgress"
                @auto-scroll-progress="handleAutoScrollProgress"
                @content-change="updateSegmentContent"
                @content-updated="handleContentUpdated"
                @warning="showTempMessage"
              />

              <voice-recorder
                v-if="idx === activeSegment && !isPlaying"
                :is-pro="isPro"
                :segment-index="idx"
                :segment="segment"
                :message-id="messageId"
                :custom-recordings="customRecordings"
                :speaker-color="getWaveBarColor(idx)"
                @recording-saved="handleRecordingSaved"
                @recording-deleted="handleRecordingDeleted"
              />
            </div>
          </transition-group>
        </div>

        <!-- Loading/Error States -->
        <div v-else-if="error" class="error-state">
          {{ error }}
        </div>
        <div v-else class="loading-state">
          Loading conversation...
        </div>
      </div>
    </div>
  </template>

  <script>
    import SegmentEditingButtons from './SegmentEditingButtons.vue';
    import CitationRenderer from '../CitationRenderer.vue';
    import VoiceRecorder from '../curated_content/VoiceRecorder.vue';
    import SectionTypeEditor from './SectionTypeEditor.vue';

  export default {
    components: {
      SegmentEditingButtons,
      CitationRenderer,
      VoiceRecorder,
      SectionTypeEditor
    },
    name: 'ConversationViewer',
    props: {
      parsedConversation: {
        type: Array,
        default: () => []
      },
      activeSegment: {
        type: Number,
        default: -1
      },
      isPlaying: {
        type: Boolean,
        default: false
      },
      isExpandedView: {
        type: Boolean,
        default: true
      },
      customRecordings: {
        type: Map,
        default: () => new Map()
      },
      isLoading: {
        type: Boolean,
        default: false
      },
      error: {
        type: String,
        default: null
      },
      messageId: {
        required: true
      },
      chatId: String,
      isPro: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        isViewerExpanded: false,
      }
    },
    methods: {
      handleSectionUpdated(data) {
        // Update your local data
        if (data.index >= 0 && data.index < this.parsedConversation.length) {
          this.$set(this.parsedConversation[data.index], 'section', data.section);
        }

        // Forward event to parent if needed
        this.$emit('section-updated', data);
      },
      activateSegment(idx, event) {
        // Don't trigger when clicking on elements that have their own interactions
        // (like buttons, links, or the citation-renderer itself if it's already editable)
        if (
          event.target.closest('button') ||
          event.target.closest('a') ||
          (idx === this.activeSegment && event.target.closest('.citation-renderer[contenteditable="true"]'))
        ) {
          return;
        }

        // Set this segment as active
        this.$emit('set-active-segment', idx);

        // If you're using local activeSegment state:
        // this.activeSegment = idx;

        // Optional: scroll the segment into view
        this.$nextTick(() => {
          const segmentEl = this.$refs[`segment_${idx}`] ||
                          document.querySelector(`.segment:nth-child(${idx + 1})`);
          if (segmentEl) {
            segmentEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      },
      handleContentUpdated(data) {
        // Forward the event to the parent with the correct parameter format
        // Use a different event name to avoid confusion
        this.$emit('segment-content-updated', data);

        // Note: We're forwarding the EXACT object we received
        // No need to reconstruct it as { index: data.index, content: data.content }
      },
      updateSegmentContent({ index, content }) {
        // Update the segment content in your data structure
        if (index >= 0 && index < this.parsedConversation.length) {
          // Create a deep copy to maintain reactivity
          const updatedConversation = JSON.parse(JSON.stringify(this.parsedConversation));
          updatedConversation[index].content = content;

          // Update your reactive data
          // If directly in component data:
          // this.parsedConversation = updatedConversation;

          // If using props from parent:
          this.$emit('update-conversation', updatedConversation);
        }
      },
      showTempMessage({ message, type }) {
        // Display a temporary notification
        // You can implement this using a toast system or simple notification
        console.log(message, type); // Replace with your actual notification logic
      },
      toggleViewer() {
        this.isViewerExpanded = !this.isViewerExpanded;
      },

      setSegmentRef(el, messageId, idx) {
        this.$emit('set-segment-ref', { el, messageId, idx });
      },

      focusContent(idx) {
        // Get the citation renderer component for this segment
        const segmentElement = this.$refs[`segment_${idx}`] ||
                              document.querySelector(`.segment:nth-child(${idx + 1})`);
        if (segmentElement) {
          const renderer = segmentElement.querySelector('.citation-renderer');
          if (renderer) {
            // Focus the contenteditable element
            renderer.focus();
          }
        }
      },

      getWaveBarColor(idx) {
        // You might want to emit an event to get this from parent
        this.$emit('get-wave-bar-color', idx);
        // Or define colors here
        const colors = [
          '#3B82F6', // Blue
          '#10B981', // Green
          '#F59E0B', // Amber
          '#EF4444', // Red
          '#8B5CF6', // Purple
          '#EC4899', // Pink
          '#14B8A6', // Teal
          '#F97316'  // Orange
        ];
        return colors[idx % colors.length];
      },

      handleSegmentDeleted(data) {
        this.$emit('segment-deleted', data);
      },

      handleSegmentAdded(data) {
        this.$emit('segment-added', data);
      },

      handleCitationClick(data) {
        this.$emit('citation-click', data);
      },

      handleCitationSpeaking(data) {
        this.$emit('citation-speaking', data);
      },

      handleAutoScrollComplete(data) {
        this.$emit('auto-scroll-complete', data);
      },

      handleFullAudioReady(data) {
        this.$emit('full-audio-ready', data);
      },

      handleAudioProgress(data) {
        this.$emit('audio-progress', data);
      },

      handleAutoScrollProgress(data) {
        this.$emit('auto-scroll-progress', data);
      },

      handleRecordingSaved(data) {
        this.$emit('recording-saved', data);
      },

      handleRecordingDeleted(data) {
        this.$emit('recording-deleted', data);
      }
    }
  }
  </script>

  <style scoped>
  .conversation-viewer {
    background-color: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 0.75rem;
    margin-bottom: 1rem;
  }

  .theme--dark .conversation-viewer {
    background-color: #1a202c;
  }

  .viewer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .viewer-title-area {
    display: flex;
    align-items: center;
  }

  .viewer-title {
    font-size: 0.875rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0;
  }

  .theme--dark .viewer-title {
    color: #e2e8f0;
  }

  .segment-count {
    font-size: 0.75rem;
    color: #718096;
    margin-left: 0.25rem;
  }

  .toggle-btn {
    padding: 0.25rem 0.375rem;
    font-size: 0.875rem;
    color: #718096;
    background: none;
    border: 1px solid #e2e8f0;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .theme--dark .toggle-btn {
    border-color: #2d3748;
    color: #a0aec0;
  }

  .toggle-btn:hover {
    background-color: #f7fafc;
  }

  .theme--dark .toggle-btn:hover {
    background-color: #2d3748;
  }

  .viewer-content {
    margin-top: 0.75rem;
  }

  .conversation {
    width: 100%;
    height: 100%;
    overflow-y: auto;
    padding: 1rem;
    background-color: white;
    border-radius: 8px;
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .theme--dark .conversation {
    background-color: #0f172a;
  }

  .segment {
    margin-bottom: 1.5rem;
    padding: 1rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    transition: all 0.2s ease;
    background-color: #ffffff;
    position: relative;
  }

  .theme--dark .segment {
    border-color: #2d3748;
    background-color: #1e293b;
  }

  .segment.active {
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }

  .segment.is-playing {
    background-color: #f0f9ff;
  }

  .theme--dark .segment.is-playing {
    background-color: #1e3a8a;
  }

  .speaker-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }

  .speaker-name {
    font-weight: 600;
    font-size: 0.9rem;
  }

  .segment-number {
    font-size: 0.75rem;
    color: #718096;
  }

  .speaker-role {
    font-size: 0.75rem;
    color: #718096;
    font-style: italic;
  }

  .custom-voice-badge {
    font-size: 0.875rem;
    margin-left: 0.25rem;
  }

  .modern-edit-indicator {
    margin-left: auto;
    padding: 0.25rem;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .modern-edit-indicator:hover {
    transform: scale(1.1);
  }

  .loading-indicator,
  .playing-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-left: auto;
  }

  .loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid #e2e8f0;
    border-top: 2px solid;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  .loading-text,
  .playing-text {
    font-size: 0.75rem;
  }

  .wave-bars {
    display: flex;
    align-items: flex-end;
    height: 16px;
    gap: 2px;
  }

  .wave-bar {
    width: 4px;
    height: 4px;
    border-radius: 2px;
    background-color: var(--wave-color);
    animation: wave 1s ease-in-out infinite;
  }

  .wave-bar:nth-child(1) { animation-delay: 0s; }
  .wave-bar:nth-child(2) { animation-delay: 0.1s; }
  .wave-bar:nth-child(3) { animation-delay: 0.2s; }

  @keyframes wave {
    0%, 100% { height: 4px; }
    50% { height: 16px; }
  }

  .error-state,
  .loading-state {
    text-align: center;
    padding: 2rem;
    color: #718096;
    font-size: 0.875rem;
  }

  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from, .fade-leave-to {
    opacity: 0;
  }

  @media (max-width: 768px) {
    .conversation {
      padding: 0.5rem;
    }

    .segment {
      padding: 0.75rem;
    }

    .speaker-info {
      flex-wrap: wrap;
    }
  }
  </style>
